import React, { useEffect, useState } from 'react';
import { logger } from '../utils/logger'
import Seo from '../components/Seo'
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { useAuthStore } from '../stores/authStore';
import { useFamilyStore } from '../stores/familyStore';
import { apiService } from '../services/api';
import { track } from '../utils/analytics';
import type { AcceptInvitationResponse } from '../services/api';
import logo from '../assets/logo/logo.png';

/**
 * Strona akceptacji zaproszenia do rodziny
 */
export const AcceptInvitation: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { initialize } = useFamilyStore();
  
  // Funkcja pomocnicza do wyświetlania nazwy roli
  const getRoleName = (role: string): string => {
    switch (role) {
      case 'parent_a':
        return 'Rodzic / Opiekun (główny)';
      case 'parent_b':
        return 'Rodzic / Opiekun';
      case 'caregiver':
        return 'Opiekun';
      case 'child_viewer':
        return 'Obserwator';
      default:
        return role;
    }
  };
  
  const [invitation, setInvitation] = useState<AcceptInvitationResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const fetchInvitation = async () => {
      if (!token) {
        setError('Nieprawidłowy token zaproszenia');
        setIsLoading(false);
        return;
      }

      try {
        logger.debug('🔍 Sprawdzanie zaproszenia przez nowe API...');
        // Dodajemy timestamp do zapytania, aby uniknąć cachowania
        const timestamp = new Date().getTime();
        const response = await apiService.acceptInvitation({ 
          token,
          _timestamp: timestamp.toString() // dodajemy jako pole, które zostanie zignorowane przez backend
        }, false);
        
        logger.debug('✅ Odpowiedź z API:', { success: response.success, hasInvitation: !!response.invitation });
        
        if (response.success && response.invitation) {
          setInvitation(response);
        } else if (response.success && response.message) {
          // Zaproszenie już zaakceptowane
          setSuccess(response.message);
        } else {
          setError('Nieprawidłowe zaproszenie');
        }
      } catch (err: any) {
        console.error('❌ Błąd pobierania zaproszenia:', err);
        setError(err.message || 'Wystąpił błąd podczas pobierania zaproszenia');
        
        // Jeśli to błąd serwera, spróbujmy ponownie maksymalnie 3 razy
        if ((err.message || '').includes('500') || (err.message || '').includes('Nieznany błąd serwera')) {
          if (retryCount < 3) {
            logger.debug(`🔄 Ponawiam próbę (${retryCount + 1}/3)...`);
            setRetryCount(prevCount => prevCount + 1);
            // Odczekaj 1 sekundę przed ponowną próbą
            setTimeout(() => {
              setError(null);
              fetchInvitation();
            }, 1000);
            return;
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (isLoading) {
      fetchInvitation();
    }
  }, [token, retryCount, isLoading]);

  // Automatycznie akceptuj zaproszenie gdy użytkownik jest zalogowany
  useEffect(() => {
    logger.debug('🔍 AcceptInvitation useEffect - automatyczna akceptacja:', {
      hasUser: !!user,
      hasInvitation: !!invitation,
      hasToken: !!token,
      success: !!success,
      hasError: !!error,
      isProcessing
    });
    
    if (user && invitation && token && !success && !error && !isProcessing) {
      logger.debug('🎯 Automatyczna akceptacja zaproszenia dla zalogowanego użytkownika');
      handleAccept();
    }
  }, [user, invitation, token, success, error, isProcessing]);

  // Dodatkowy useEffect - nasłuchuje na zmianę stanu autoryzacji po załadowaniu zaproszenia
  useEffect(() => {
    logger.debug('👤 User state changed useEffect:', { hasUser: !!user, hasInvitation: !!invitation });
    
    // Jeśli użytkownik się właśnie zalogował i mamy zaproszenie, spróbuj zaakceptować
    if (user && invitation && token && !success && !error && !isProcessing) {
      logger.debug('🚀 Użytkownik się zalogował - triggerowanie akceptacji zaproszenia');
      setTimeout(() => {
        handleAccept();
      }, 100); // Krótkie opóźnienie żeby upewnić się że stan jest stabilny
    }
  }, [user?.id]); // Nasłuchuje tylko na zmianę ID użytkownika

  const handleAccept = async () => {
    if (!user || !invitation || !token) return;

    setIsProcessing(true);
    setError(null);
    
    try {
      logger.debug('🔍 Akceptacja zaproszenia przez nowe API');
      
      // Wyślij token autoryzacji dla zalogowanych użytkowników
      const response = await apiService.acceptInvitation({
        token,
        userId: user.id
      }, true); // true = wymagany Authorization header
      
      logger.debug('✅ Wynik akceptacji zaproszenia:', { success: response.success });
      
      if (response.success) {
        track('invite_accepted', { role: invitation.invitation?.role });
        setSuccess('Zaproszenie zostało zaakceptowane. Za chwilę zostaniesz przekierowany.');
        
        // Reinicjalizuj dane rodziny
        logger.debug('🔄 Reinicjalizacja danych rodziny...');
        await initialize();
        logger.debug('✅ Dane rodziny zainicjalizowane');
        
        // Przekieruj do kalendarza po krótkim opóźnieniu
        setTimeout(() => {
          navigate('/calendar');
        }, 2000);
      } else {
        setError('Nie udało się zaakceptować zaproszenia');
      }
    } catch (err: any) {
      console.error('❌ Błąd akceptacji zaproszenia');

      // Obsługa błędu 401 - wygaśnięcie sesji
      if ((err.message || '').includes('401') || err.requiresAuth) {
        setError('Sesja wygasła. Zaloguj się ponownie i spróbuj ponownie zaakceptować zaproszenie.');
      } else {
        setError(err.message || 'Wystąpił błąd podczas akceptacji zaproszenia');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!token) return;

    setIsProcessing(true);
    setError(null);
    
    try {
      logger.debug('🔍 Odrzucanie zaproszenia przez nowe API');
      
      const response = await apiService.rejectInvitation(token, !!user);
      
      logger.debug('✅ Wynik odrzucenia zaproszenia:', { success: response.success });
      
      if (response.success) {
        setSuccess('Zaproszenie zostało odrzucone. Za chwilę zostaniesz przekierowany.');
        
        // Przekieruj do strony głównej po krótkim opóźnieniu
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        setError('Nie udało się odrzucić zaproszenia');
      }
    } catch (err: any) {
      console.error('❌ Błąd odrzucenia zaproszenia');
      setError(err.message || 'Wystąpił błąd podczas odrzucenia zaproszenia');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRetry = () => {
    setIsLoading(true);
    setError(null);
    setRetryCount(0);
  };

  return (
    <Layout>
      <Seo title="Zaproszenie — 2domy.pl" robots="noindex, nofollow, noarchive" canonical="https://2domy.pl/invitation/[token]" />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-6">
          <h1 className="text-2xl font-bold mb-6 text-center">Zaproszenie do rodziny</h1>
          
          {isLoading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
              <p>Sprawdzanie zaproszenia...</p>
            </div>
          ) : error ? (
            <div className="text-center py-4">
              <div className="text-red-500 text-5xl mb-4">⚠️</div>
              <h2 className="text-xl font-semibold mb-2">Błąd</h2>
              <p className="text-gray-600 mb-4">{error}</p>
              <div className="space-y-3">
                <button
                  onClick={handleRetry}
                  className="btn btn-primary"
                >
                  Spróbuj ponownie
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="btn btn-outline"
                >
                  Wróć do strony głównej
                </button>
              </div>
            </div>
          ) : success ? (
            <div className="text-center py-4">
              <div className="text-green-500 text-5xl mb-4">✅</div>
              <h2 className="text-xl font-semibold mb-2">Sukces</h2>
              <p className="text-gray-600">{success}</p>
            </div>
          ) : invitation ? (
            <div className="space-y-6">
              <div className="text-center">
              <div className="mb-4">
                <img src={logo} alt="2domy.pl" className="h-12 w-12 rounded-md shadow-sm mx-auto" />
              </div>
                <h2 className="text-xl font-semibold mb-2">Zaproszenie do rodziny</h2>
                <p className="text-gray-600">
                  Zostałeś zaproszony do dołączenia do rodziny jako{' '}
                  <span className="font-medium">{invitation.invitation ? getRoleName(invitation.invitation.role) : ''}</span>.
                </p>
              </div>
              
              <div className="border-t border-b py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{invitation.invitation?.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Wygasa</p>
                    <p className="font-medium">
                      {invitation.invitation ? new Date(invitation.invitation.expires_at).toLocaleDateString() : ''}
                    </p>
                  </div>
                </div>
              </div>
              
              {!user ? (
                <div className="text-center">
                  <p className="text-gray-600 mb-4">
                    Musisz utworzyć konto, aby zaakceptować zaproszenie.
                  </p>
                  <div className="space-y-3">
                    <button
                      onClick={() => navigate(`/auth/register?redirect=/invitation/${token}`)}
                      className="w-full neon-button"
                    >
                      Utwórz konto
                    </button>
                    <p className="text-sm text-gray-500">
                      Lub{' '}
                      <button
                        onClick={() => navigate(`/auth/login?redirect=/invitation/${token}`)}
                        className="text-blue-600 hover:underline"
                      >
                        zaloguj się
                      </button>
                      {' '}jeśli już masz konto
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex space-x-4">
                  <button
                    onClick={handleReject}
                    disabled={isProcessing}
                    className="btn btn-outline flex-1"
                  >
                    {isProcessing ? 'Przetwarzanie...' : 'Odrzuć'}
                  </button>
                  <button
                    onClick={handleAccept}
                    disabled={isProcessing}
                    className="btn btn-primary flex-1"
                  >
                    {isProcessing ? 'Przetwarzanie...' : 'Akceptuj'}
                  </button>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </Layout>
  );
};